import React, { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import { Activity, Users, Award, BarChart3, Trophy } from 'lucide-react';
import { labProgressApi } from '../../lib/api';

export const LabAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    labProgressApi.getAnalytics().then(data => {
      setAnalytics(data);
    }).catch(err => {
      console.error('Failed to fetch lab analytics:', err);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="skeleton h-64 rounded-lg" />;

  const data = analytics || {};
  const labStats = data.lab_stats || [];
  const topStudents = data.top_students || [];

  return (
    <div className="space-y-6" data-testid="lab-analytics">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6" /> Lab Analytics
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Track student engagement and progress across all labs.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl border bg-card">
          <div className="text-2xl font-bold">{data.total_active_students || 0}</div>
          <div className="text-sm text-muted-foreground">Active Students</div>
        </div>
        <div className="p-4 rounded-xl border bg-card">
          <div className="text-2xl font-bold">{data.total_labs_with_activity || 0}</div>
          <div className="text-sm text-muted-foreground">Labs with Activity</div>
        </div>
        <div className="p-4 rounded-xl border bg-card">
          <div className="text-2xl font-bold">{labStats.reduce((s, l) => s + l.total_completions, 0)}</div>
          <div className="text-sm text-muted-foreground">Total Mission Completions</div>
        </div>
        <div className="p-4 rounded-xl border bg-card">
          <div className="text-2xl font-bold">{labStats.reduce((s, l) => s + l.total_xp, 0)}</div>
          <div className="text-sm text-muted-foreground">Total XP Earned</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Lab Popularity Table */}
        <div className="border rounded-xl overflow-hidden">
          <div className="p-4 bg-muted/50 border-b">
            <h3 className="font-semibold">Lab Engagement</h3>
          </div>
          <div className="overflow-auto max-h-[400px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-4 py-2.5">Lab</th>
                  <th className="text-center px-3 py-2.5">Students</th>
                  <th className="text-center px-3 py-2.5">Missions</th>
                  <th className="text-center px-3 py-2.5">XP</th>
                </tr>
              </thead>
              <tbody>
                {labStats.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No lab activity yet</td></tr>
                )}
                {labStats.map((lab, i) => (
                  <tr key={lab.lab_id} className="border-b hover:bg-muted/20">
                    <td className="px-4 py-2.5 font-medium">{lab.lab_id}</td>
                    <td className="px-3 py-2.5 text-center">{lab.student_count}</td>
                    <td className="px-3 py-2.5 text-center">{lab.total_completions}</td>
                    <td className="px-3 py-2.5 text-center font-medium text-yellow-500">{lab.total_xp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Students Leaderboard */}
        <div className="border rounded-xl overflow-hidden">
          <div className="p-4 bg-muted/50 border-b">
            <h3 className="font-semibold flex items-center gap-2"><Trophy className="w-4 h-4 text-yellow-500" /> Top Students</h3>
          </div>
          <div className="overflow-auto max-h-[400px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-4 py-2.5">#</th>
                  <th className="text-left px-3 py-2.5">Student</th>
                  <th className="text-center px-3 py-2.5">Grade</th>
                  <th className="text-center px-3 py-2.5">Labs</th>
                  <th className="text-center px-3 py-2.5">Missions</th>
                  <th className="text-center px-3 py-2.5">XP</th>
                </tr>
              </thead>
              <tbody>
                {topStudents.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No student activity yet</td></tr>
                )}
                {topStudents.map((student, i) => (
                  <tr key={student.user_id} className="border-b hover:bg-muted/20">
                    <td className="px-4 py-2.5">
                      <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-500 text-white' : i === 1 ? 'bg-gray-400 text-white' : i === 2 ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground'}`}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-medium">{student.name}</td>
                    <td className="px-3 py-2.5 text-center">{student.grade ? `Class ${student.grade}` : '-'}</td>
                    <td className="px-3 py-2.5 text-center">{student.labs_count}</td>
                    <td className="px-3 py-2.5 text-center">{student.total_missions}</td>
                    <td className="px-3 py-2.5 text-center font-bold text-yellow-500">{student.total_xp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};


